import { MortgageData } from '@/components/calculator/CalculatorTabs';
import { BuydownScenario } from '@/components/calculator/results/utils/buydownCalculations';
import { OptimizationOpportunity } from '@/types/analytics';

export const identifyOptimizationOpportunities = (
  mortgageData: MortgageData,
  buydownScenarios: BuydownScenario[]
): OptimizationOpportunity[] => {
  const opportunities: OptimizationOpportunity[] = [];
  const loanAmount = mortgageData.homePrice - mortgageData.downPayment;

  // Down Payment Optimization
  if (mortgageData.downPaymentPercentage < 20) {
    const additional = mortgageData.homePrice * 0.20 - mortgageData.downPayment;
    const pmiSavings = mortgageData.monthlyPayment.pmi * 12 * 5;
    opportunities.push({
      id: 'down-payment-20',
      category: 'downPayment',
      title: 'Increase Down Payment to 20%',
      description: `Adding $${additional.toLocaleString()} to reach 20% down would eliminate PMI and reduce your interest rate.`,
      potentialSavings: pmiSavings,
      implementationDifficulty: 'Hard',
      priority: 'High',
      actionItems: [
        `Save an additional $${additional.toLocaleString()}`,
        'Explore gift funds from family members',
        'Consider delaying purchase to build savings',
        'Look into down payment assistance programs'
      ]
    });
  }

  // Buydown Strategy
  if (buydownScenarios.length > 0) {
    const bestBuydown = buydownScenarios.reduce((best, current) => {
      const bestSavings = best.totalSavings || 0;
      const currentSavings = current.totalSavings || 0;
      return currentSavings > bestSavings ? current : best;
    });

    if (bestBuydown.type !== 'fixed' && (bestBuydown.totalSavings || 0) > 5000) {
      opportunities.push({
        id: 'optimal-buydown',
        category: 'buydown',
        title: `Switch to ${bestBuydown.name}`,
        description: `The ${bestBuydown.name} strategy could save you $${(bestBuydown.totalSavings || 0).toLocaleString()} over the first few years.`,
        potentialSavings: bestBuydown.totalSavings || 0,
        implementationDifficulty: 'Easy',
        priority: 'High',
        actionItems: [
          'Review the Rate & Points Configuration section',
          'Compare all buydown scenarios',
          'Discuss builder incentive options with your loan officer',
          'Ensure you plan to stay in the home long enough to benefit'
        ]
      });
    }
  }

  // Rate Reduction
  if (mortgageData.interestRate > 6.5) {
    const rateSavings = loanAmount * 0.005 * mortgageData.loanTerm;
    opportunities.push({
      id: 'rate-shopping',
      category: 'rate',
      title: 'Shop for Better Interest Rate',
      description: 'Your current rate is above market average. Shopping with multiple lenders could reduce your rate by 0.25-0.5%.',
      potentialSavings: rateSavings,
      implementationDifficulty: 'Medium',
      priority: 'High',
      actionItems: [
        'Get quotes from at least 3 different lenders',
        'Check your credit score and improve if possible',
        'Compare both rate and closing costs',
        'Negotiate with lenders using competing offers'
      ]
    });
  }

  // Closing Cost Negotiation
  const closingCostRatio = (mortgageData.closingCosts.total / mortgageData.homePrice) * 100;
  if (closingCostRatio > 3) {
    const potentialSavings = mortgageData.closingCosts.total * 0.15;
    opportunities.push({
      id: 'closing-costs',
      category: 'payment',
      title: 'Negotiate Closing Costs',
      description: 'Your closing costs are above average. You may be able to negotiate lower fees or ask the seller for credits.',
      potentialSavings,
      implementationDifficulty: 'Medium',
      priority: 'Medium',
      actionItems: [
        'Review the itemized closing cost breakdown',
        'Compare fees across multiple lenders',
        'Ask about lender credits in exchange for slightly higher rate',
        'Request seller concessions in your offer'
      ]
    });
  }

  // Loan Type Optimization
  const loanToValue = (loanAmount / mortgageData.homePrice) * 100;
  if (mortgageData.loanType === 'conventional' && loanToValue > 80) {
    opportunities.push({
      id: 'loan-type-fha',
      category: 'loanType',
      title: 'Consider FHA Loan',
      description: 'With less than 20% down, an FHA loan might offer better terms and lower PMI costs.',
      potentialSavings: 0,
      implementationDifficulty: 'Medium',
      priority: 'Medium',
      actionItems: [
        'Compare conventional vs FHA for your situation',
        'Check FHA loan limits in your area',
        'Consider FHA\'s upfront and monthly mortgage insurance',
        'Discuss with your loan officer'
      ]
    });
  }

  // Opportunity 6: Energy Efficiency Value
  if (mortgageData.homePrice > 300000) {
    opportunities.push({
      id: 'energy-efficiency',
      category: 'payment',
      title: 'Consider Energy Efficient Homes',
      description: 'Energy-efficient homes (lower HERS Index) can offset higher purchase prices with utility savings. A home with HERS 70 vs HERS 100 could save $1,500+ annually on utilities.',
      potentialSavings: 1500 * mortgageData.loanTerm,
      implementationDifficulty: 'Medium',
      priority: 'Medium',
      actionItems: [
        'Request HERS rating during home inspection',
        'Compare utility costs of similar homes in the area',
        'Ask builders about energy efficiency features',
        'Use the Energy Efficiency calculator in Analytics Dashboard',
        'Factor utility savings into your monthly budget calculations'
      ]
    });
  }

  return opportunities.sort((a, b) => {
    const priorityOrder = { 'High': 0, 'Medium': 1, 'Low': 2 };
    return priorityOrder[a.priority] - priorityOrder[b.priority];
  });
};
